/**
 * Freeze V2 — Metrics Tab
 *
 * Summary statistics for the current freeze session.
 * Matches v1 FreezeMetrics: totals, quality embryos, and
 * CONV/SF/RS cleavage + QEDR rates.
 */
import { useMemo } from 'react';

import { FieldLabel } from '@/components/ui/field';
import type {
  FertAndEmbryosResponse,
  FertilizationListFreezeResponse,
} from '@/services/freeze';

import { useFreezeSelection } from '../../contexts';

type MetricsTabProps = {
  fertilizations: FertilizationListFreezeResponse[];
  embryosByFert: Map<string, FertAndEmbryosResponse>;
};

export function MetricsTab({ fertilizations, embryosByFert }: MetricsTabProps) {
  const selection = useFreezeSelection();

  const { totals, rates } = useMemo(() => {
    const t = {
      totalMatings: fertilizations.length,
      matingsWithLive: 0,
      totalEmbryos: 0,
      frozenEmbryos: 0,
      qualityEmbryosToFreeze: 0,
      totalLive: 0,
      totalTemp: 0,
      totalDiscarded: 0,
      totalDegen: 0,
      totalNEP: 0,
      totalD65: 0,
      totalD7: 0,
      convIvc: 0,
      convCleaved: 0,
      convQualityEmbryos: 0,
      sfIvc: 0,
      sfCleaved: 0,
      sfQualityEmbryos: 0,
      rsIvc: 0,
      rsCleaved: 0,
      rsQualityEmbryos: 0,
    };

    for (const fert of fertilizations) {
      if ((fert.embs ?? 0) > 0) t.matingsWithLive++;

      const quality = fert.predictedGood ?? 0;
      const low = fert.predictedVeryLow ?? 0;
      t.totalEmbryos += quality + low;
      t.frozenEmbryos += fert.embs ?? 0;
      t.qualityEmbryosToFreeze += quality;
      t.totalDiscarded +=
        (fert.discardedGrade1 ?? 0) +
        (fert.discardedGrade2 ?? 0) +
        (fert.discarded ?? 0);
      t.totalDegen += fert.degenerates ?? 0;
      if (fert.noEmbryosProduced) t.totalNEP++;

      const semenType = fert.semenType ?? '';
      if (semenType.startsWith('RS')) {
        t.rsIvc += fert.ivcNumber ?? 0;
        t.rsCleaved += fert.cleaved ?? 0;
        t.rsQualityEmbryos += quality;
      } else if (semenType.includes('SF')) {
        t.sfIvc += fert.ivcNumber ?? 0;
        t.sfCleaved += fert.cleaved ?? 0;
        t.sfQualityEmbryos += quality;
      } else {
        t.convIvc += fert.ivcNumber ?? 0;
        t.convCleaved += fert.cleaved ?? 0;
        t.convQualityEmbryos += quality;
      }

      const embryoData = embryosByFert.get(fert.fertId ?? '');
      if (embryoData) {
        t.totalLive += embryoData.liveEmbryos.length;
        t.totalTemp += embryoData.tempEmbryos.length;
        for (const e of embryoData.liveEmbryos) {
          if (e.d65 === 1) t.totalD65++;
          if (e.d7 === 1) t.totalD7++;
        }
        for (const e of embryoData.tempEmbryos) {
          if (e.d65 === 1) t.totalD65++;
          if (e.d7 === 1) t.totalD7++;
        }
      }
    }

    const r = {
      convCleavageRate: t.convIvc ? (t.convCleaved / t.convIvc) * 100 : 0,
      convQedrRate: t.convIvc ? (t.convQualityEmbryos / t.convIvc) * 100 : 0,
      sfCleavageRate: t.sfIvc ? (t.sfCleaved / t.sfIvc) * 100 : 0,
      sfQedrRate: t.sfIvc ? (t.sfQualityEmbryos / t.sfIvc) * 100 : 0,
      rsCleavageRate: t.rsIvc ? (t.rsCleaved / t.rsIvc) * 100 : 0,
      rsQedrRate: t.rsIvc ? (t.rsQualityEmbryos / t.rsIvc) * 100 : 0,
    };

    return { totals: t, rates: r };
  }, [fertilizations, embryosByFert]);

  if (fertilizations.length === 0) {
    return (
      <p className="py-4 text-sm text-gray-400">
        Load fertilizations to see metrics.
      </p>
    );
  }

  const selectedEmbryoCount =
    selection.liveEmbryoIds.length + selection.tempEmbryoIds.length;
  const selectedFertCount = selection.fertilizationIds.length;

  return (
    <div className="flex flex-col gap-3">
      <FieldLabel>Session Summary</FieldLabel>

      <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
        <MetricCard label="Matings" value={totals.totalMatings} />
        <MetricCard
          label="Matings w/ Live Embs"
          value={totals.matingsWithLive}
        />
        <MetricCard label="Embs" value={totals.totalEmbryos} />
        <MetricCard label="Embs Frozen Via App" value={totals.frozenEmbryos} />
        <MetricCard
          label="Total Quality Embs Holding"
          value={totals.qualityEmbryosToFreeze}
        />
      </div>

      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7">
        <MetricCard label="Live Embryos" value={totals.totalLive} />
        <MetricCard label="Temp Embryos" value={totals.totalTemp} />
        <MetricCard label="Total Discarded" value={totals.totalDiscarded} />
        <MetricCard label="Degenerates" value={totals.totalDegen} />
        <MetricCard label="NEP" value={totals.totalNEP} />
        <MetricCard label="D6.5 Embryos" value={totals.totalD65} />
        <MetricCard label="D7 Embryos" value={totals.totalD7} />
      </div>

      <div>
        <h4 className="mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase dark:text-gray-400">
          Cleavage &amp; QEDR Rates
        </h4>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
          <MetricCard
            label="Clv% (CONV)"
            value={`${rates.convCleavageRate.toFixed(1)}%`}
          />
          <MetricCard
            label="QEDR% (CONV)"
            value={`${rates.convQedrRate.toFixed(1)}%`}
          />
          <MetricCard
            label="Clv% (SF)"
            value={`${rates.sfCleavageRate.toFixed(1)}%`}
          />
          <MetricCard
            label="QEDR% (SF)"
            value={`${rates.sfQedrRate.toFixed(1)}%`}
          />
          <MetricCard
            label="Clv% (RS)"
            value={`${rates.rsCleavageRate.toFixed(1)}%`}
          />
          <MetricCard
            label="QEDR% (RS)"
            value={`${rates.rsQedrRate.toFixed(1)}%`}
          />
        </div>
      </div>

      <div className="text-xs text-gray-500 dark:text-gray-400">
        <strong>{selectedEmbryoCount}</strong> embryos from{' '}
        <strong>{selectedFertCount}</strong>{' '}
        {selectedFertCount === 1 ? 'mating' : 'matings'} selected
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) {
  return (
    <div className="flex flex-col items-center rounded-md border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-800">
      <span className="text-xl font-bold text-gray-900 dark:text-gray-100">
        {value}
      </span>
      <span className="text-center text-xs text-gray-500 dark:text-gray-400">
        {label}
      </span>
    </div>
  );
}
